import React, {useEffect, useMemo, useRef, useState} from "react";
import "./GMap.css";
import {
  ChargingStationPOI,
  ConstructionSitePOI,
  PointOfInterest,
  WifiHotspotPOI,
} from "../logic/APITypes";
import {useMap} from "@vis.gl/react-google-maps";
import {MarkerClusterer} from "@googlemaps/markerclusterer";
import type {Marker} from "@googlemaps/markerclusterer";
import MarkerCircle from "../components/MarkerCircle";

function GMap(props: {
  openPoi: (poi: PointOfInterest) => void;
  constructionPois: ConstructionSitePOI[];
  chargingPois: ChargingStationPOI[];
  wifiPois: WifiHotspotPOI[];
}) {
  const map = useMap();

  const [constructionMarkers, setConstructionMarkers] = useState<{
    [key: string]: Marker;
  }>({});
  const [chargingMarkers, setChargingMarkers] = useState<{
    [key: string]: Marker;
  }>({});
  const constructionClusterer = useRef<MarkerClusterer | null>(null);
  const chargingClusterer = useRef<MarkerClusterer | null>(null);

  useEffect(() => {
    if (!map) return;
    if (!constructionClusterer.current)
      constructionClusterer.current = new MarkerClusterer({map});
    if (!chargingClusterer.current)
      chargingClusterer.current = new MarkerClusterer({map});
  }, [map]);

  useEffect(() => {
    constructionClusterer.current?.clearMarkers();
    constructionClusterer.current?.addMarkers(
      Object.values(constructionMarkers)
    );
  }, [constructionMarkers]);

  useEffect(() => {
    chargingClusterer.current?.clearMarkers();
    chargingClusterer.current?.addMarkers(Object.values(chargingMarkers));
  }, [chargingMarkers]);

  const mapComponent = useMemo(() => {
    const setMarkerRef = (
      type: "construction" | "charging",
      marker: Marker | null,
      key: string
    ) => {
      const markers =
        type === "construction" ? constructionMarkers : chargingMarkers;
      const setMarkers =
        type === "construction" ? setConstructionMarkers : setChargingMarkers;
      if (marker && markers[key]) return;
      if (!marker && !markers[key]) return;

      setMarkers((prev) => {
        if (marker) {
          return {...prev, [key]: marker};
        } else {
          const newMarkers = {...prev};
          delete newMarkers[key];
          return newMarkers;
        }
      });
    };
    return (
      <>
        {props.chargingPois.map((chargingPoi) => (
          <MarkerCircle
            key={chargingPoi.id}
            chargingPoi={chargingPoi}
            openPoi={props.openPoi}
            setMarkerRef={setMarkerRef}
          />
        ))}
      </>
    );
  }, [props, chargingMarkers, constructionMarkers]);

  return mapComponent;
}

export default GMap;
